import { find as _find } from 'lodash-es';
import { useState, Fragment, useMemo, useCallback } from 'react';
import { useMutation } from '@apollo/client/react';
import type { User } from '../../types/User';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import NotificationsActive from '@mui/icons-material/NotificationsActive';
import NotificationsNone from '@mui/icons-material/NotificationsNone';
import { makeStyles } from 'tss-react/mui';
import { updateUserFollowMutation } from '../../apollo/users';
import { useAppContext } from '../../contexts/app';

const useStyles = makeStyles()((theme) => ({
  button: {
    padding: theme.spacing(0, 1),
    minWidth: 'auto',
  },
}));

type Props = {
  user: User;
  className?: string;
  [key: string]: unknown;
};

const UserNotificationPreferenceToggle = ({ user, className, ...rest }: Props) => {
  const [submitting, setSubmitting] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { classes, cx } = useStyles();
  const { isLoggedIn, loggedInUser } = useAppContext();

  const [updateUserFollow] = useMutation(updateUserFollowMutation);

  const userFollow = useMemo(() => {
    if (!loggedInUser) {
      return null;
    }
    return _find(loggedInUser.userFollows, ({ followingUser: { id: followingUserId } }) => (
      followingUserId === user.id
    ));
  }, [loggedInUser, user]);
  const notificationsOn = userFollow && userFollow.notificationPreference === 'ALL';

  const handleToggle = useCallback(() => {
    setAnchorEl(null);
    setSubmitting(true);
    updateUserFollow({
      variables: {
        userId: user.id,
        data: {
          notificationPreference: notificationsOn ? 'NONE' : 'ALL',
        },
      },
    }).then(() => setSubmitting(false));
  }, [user, notificationsOn, updateUserFollow]);

  if (!isLoggedIn || !userFollow) {
    return null;
  }

  const disabled = submitting;
  const menuId = `user-notification-preference-menu-${user.id}`;

  return (
    <Fragment>
      <Button
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
        }}
        disabled={disabled}
        aria-controls={menuId}
        aria-haspopup="true"
        color="primary"
        className={cx(classes.button, className)}
        {...rest}
      >
        {notificationsOn ? <NotificationsActive /> : <NotificationsNone />}
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
        }}
      >
        <MenuItem
          onClick={handleToggle}
        >
          {notificationsOn ?
            'Unsubscribe from new events' :
            'Subscribe to new events'}
        </MenuItem>
      </Menu>
    </Fragment>
  );
};

export default UserNotificationPreferenceToggle;
